import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowLeftRight,
  Banknote,
  Database,
  Download,
  ExternalLink,
  Globe,
  KeyRound,
  Pause,
  Pencil,
  RefreshCw,
  Trash2,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Tabs } from '../../components/Tabs';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { PosRoleSelector } from '../../components/PosRoleSelector';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import { CashPaymentModal } from '../../components/CashPaymentModal';
import { LedgerView } from '../../components/LedgerView';
import { toMajor, toMinor } from '../../contracts/money';
import { useAdminBusinesses, useAdminBusiness, useAdminAllPackages, useAdminAllPrices, setBusinessStatus, refundPayment, changeBusinessPlan, createAdminBusinessUser, updateAdminBusinessInfo, updateAdminBusinessUser, removeAdminBusinessUser, adminResetUserPassword, type AdminBusinessDetail, type InviteResult, type CreateUserResult } from '../../api/admin';
import { useAdminBusinessLedger } from '../../api/billing';
import { openBusinessPosAsAdmin } from '../../api/pos';
import { formatDate, timeAgo } from '../../lib/format';

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success', trial: 'info', past_due: 'warning', suspended: 'error',
  canceled: 'neutral', pending_payment: 'warning', provisioning: 'info', deleted: 'neutral',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Active', trial: 'Trial', past_due: 'Past due', suspended: 'Suspended',
  canceled: 'Canceled', pending_payment: 'Pending pay', provisioning: 'Provisioning', deleted: 'Deleted',
};
const DB_LABEL: Record<string, string> = { managed: 'Managed DB', self_hosted: 'Self-hosted DB', legacy: 'Legacy (shared)' };

/** Business detail — header + 7 tabs (Overview, Subscription, Payments, Database, Domains, Users, Audit). */
export function BusinessDetail() {
  const { data: adminBusinesses = [] } = useAdminBusinesses();
  // Select the routed business (the page used to always show adminBusinesses[0]).
  const params = useParams({ strict: false }) as { id?: string };
  const b = adminBusinesses.find((x) => x.id === params.id);
  // Full drill-in (real DB/subscription/payments) — the list only carries the header fields.
  const { data: detail } = useAdminBusiness(params.id ?? '');
  const { data: ledger } = useAdminBusinessLedger(params.id ?? '');
  const qc = useQueryClient();
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [posBusy, setPosBusy] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  async function openPosAsAdmin() {
    if (!b || posBusy) return;
    setPosBusy(true);
    try {
      await openBusinessPosAsAdmin(b.id, '/home');
    } finally {
      setPosBusy(false);
    }
  }

  async function setStatus(status: Parameters<typeof setBusinessStatus>[1]) {
    const id = params.id;
    if (!id || statusBusy) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      await setBusinessStatus(id, status);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'business', id] });
    } catch (e) {
      // Previously swallowed — a failed Suspend/Activate looked like a no-op. Surface it.
      setStatusError(e instanceof Error ? e.message : 'Could not change status. Please try again.');
    } finally {
      setStatusBusy(false);
    }
  }

  // ── Refund a payment (claws back reseller commission) ──────────────────────
  const refundablePayments = (detail?.payments ?? []).filter((p) => p.status === 'paid');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundAmount, setRefundAmount] = useState(''); // major units (৳)
  const [refundReason, setRefundReason] = useState('');
  const [refundChannel, setRefundChannel] = useState<'refund' | 'chargeback'>('refund');
  const [refundError, setRefundError] = useState<string | null>(null);
  const refundTarget = refundablePayments.find((p) => p.id === refundPaymentId);

  function openRefund() {
    const target = refundablePayments[0];
    if (!target) return;
    setRefundPaymentId(target.id);
    setRefundAmount(String(toMajor(target.amountMinor)));
    setRefundReason('');
    setRefundChannel('refund');
    setRefundError(null);
    setRefundOpen(true);
  }

  async function doRefund() {
    const id = params.id;
    const amountMinor = toMinor(Number(refundAmount));
    if (!refundPaymentId || !Number.isFinite(amountMinor) || amountMinor <= 0) {
      setRefundError('Enter a valid amount.');
      return;
    }
    setRefundBusy(true);
    setRefundError(null);
    try {
      await refundPayment(refundPaymentId, {
        amountMinor,
        reason: refundReason.trim() || undefined,
        channel: refundChannel,
      });
      if (id) void qc.invalidateQueries({ queryKey: ['admin', 'business', id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
      setRefundOpen(false);
    } catch (e) {
      setRefundError(e instanceof Error ? e.message : 'Refund failed.');
    } finally {
      setRefundBusy(false);
    }
  }

  if (adminBusinesses.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }
  if (!b) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Business not found.
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link
        to="/admin/businesses"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        All businesses
      </Link>

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <span className="w-12 h-12 rounded-lg bg-zinc-950 dark:bg-zinc-50 grid place-items-center text-base font-semibold text-white dark:text-zinc-950 flex-shrink-0">
              {b.name.split(' ').slice(0, 2).map((p) => p.charAt(0)).join('')}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 truncate">
                  {b.name}
                </h1>
                <Badge tone={STATUS_TONE[b.status] ?? 'neutral'}>{STATUS_LABEL[b.status] ?? b.status}</Badge>
                {b.paymentReviewAt && <Badge tone="warning">Review</Badge>}
                <span className="text-xs text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">{b.plan} · {DB_LABEL[b.dbMode] ?? b.dbMode}</span>
              </div>
              <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
                Reseller: {b.resellerName ?? 'Direct'} · Owner: {b.ownerEmail} · <span className="mono">business_id: {b.id.replace('biz_', '')}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" size="sm" iconLeft={<ExternalLink className="w-4 h-4" />} loading={posBusy} onClick={openPosAsAdmin}>
              Open POS
            </Button>
            <Button size="sm" iconLeft={<Banknote className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record payment
            </Button>
            {b.status === 'suspended' ? (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => setStatus('active')}>
                Activate
              </Button>
            ) : (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => setStatus('suspended')}>
                Suspend
              </Button>
            )}
            <Button variant="secondary" size="sm" iconLeft={<XCircle className="w-4 h-4" />} disabled={statusBusy || b.status === 'canceled'} onClick={() => setStatus('canceled')}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={refundablePayments.length === 0}
              title={refundablePayments.length === 0 ? 'No refundable (paid) payments' : undefined}
              onClick={openRefund}
            >
              Refund
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Pencil className="w-4 h-4" />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </div>
        </div>
        {statusError && (
          <p className="mt-3 text-sm text-rose-600 dark:text-rose-400 lg:text-right">{statusError}</p>
        )}
      </Card>

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview', content: <OverviewTab detail={detail} onChangePlan={() => setPlanOpen(true)} /> },
            { id: 'subscription', label: 'Subscription', content: <SubscriptionTab sub={detail?.subscription ?? null} onChangePlan={() => setPlanOpen(true)} /> },
            { id: 'payments', label: 'Payments', count: detail?.payments?.length, content: <PaymentsTab payments={detail?.payments ?? []} /> },
            { id: 'ledger', label: 'Ledger', count: ledger?.rows.length, content: <LedgerView ledger={ledger} /> },
            { id: 'database', label: 'Database', content: <DatabaseTab db={detail?.database ?? null} /> },
            { id: 'domains', label: 'Domains', count: detail?.domains?.length, content: <DomainsTab domains={detail?.domains ?? []} /> },
            { id: 'users', label: 'Users', count: detail?.users?.length, content: <UsersTab users={detail?.users ?? []} businessId={params.id ?? ''} onChanged={() => { if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] }); }} /> },
            { id: 'audit', label: 'Audit', count: detail?.audit?.length, content: <AuditTab audit={detail?.audit ?? []} /> },
          ]}
        />
      </Card>

      <EditBusinessModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        businessId={b.id}
        initialName={b.name}
        initialOwnerEmail={detail?.ownerEmail ?? b.ownerEmail ?? ''}
        onChanged={() => {
          if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
        }}
      />

      <ChangePlanModal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        businessId={b.id}
        businessName={b.name}
        sub={detail?.subscription ?? null}
        onChanged={() => {
          if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
        }}
      />

      <CashPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        businessId={b.id}
        businessName={b.name}
        onRecorded={() => {
          if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
        }}
      />

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Issue refund"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={refundBusy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={doRefund} loading={refundBusy}>
              {refundChannel === 'chargeback' ? 'Record chargeback' : 'Issue refund'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {refundTarget && (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Refunding{' '}
              <span className="font-medium text-zinc-900 dark:text-zinc-100 mono">{refundTarget.invoiceNo}</span>{' '}
              (<Money minor={refundTarget.amountMinor} hideCents tone="neutral" /> · {refundTarget.method}). This claws
              back the reseller commission for this payment.
            </p>
          )}
          <Input label="Amount (৳)" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
          <Input
            label="Reason (optional)"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="e.g. duplicate charge"
          />
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Channel</span>
            <div className="flex gap-2">
              {(['refund', 'chargeback'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setRefundChannel(c)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition capitalize ${
                    refundChannel === c
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {refundError && <p className="text-sm text-rose-600 dark:text-rose-400">{refundError}</p>}
        </div>
      </Modal>
    </div>
  );
}

function OverviewTab({ detail, onChangePlan }: { detail: AdminBusinessDetail | undefined; onChangePlan: () => void }) {
  const sub = detail?.subscription;
  const db = detail?.database;
  const audit = detail?.audit ?? [];
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader
            title="Subscription"
            action={sub ? <Button variant="secondary" size="sm" iconLeft={<ArrowLeftRight className="w-3.5 h-3.5" />} onClick={onChangePlan}>Change plan</Button> : undefined}
          />
          {sub ? (
            <dl className="space-y-2.5 text-sm">
              <Row k="Plan" v={`${sub.plan ?? '—'} · ${taka(sub.unitAmountMinor)}/${sub.interval}`} />
              <Row k="Status" v={<Badge tone={sub.status === 'active' ? 'success' : sub.status === 'trial' ? 'info' : sub.status === 'past_due' ? 'warning' : 'error'}>{sub.status}</Badge>} />
              <Row k="Renews" v={<span className="tabular-nums">{sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}</span>} />
            </dl>
          ) : (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">No subscription on record.</p>
          )}
        </Card>

        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Database" />
          {db ? (
            <dl className="space-y-2.5 text-sm">
              <Row k="Mode" v={db.mode === 'self_hosted' ? 'Self-hosted' : 'Managed'} />
              <Row k="Status" v={db.status} />
              <Row k="Host" v={<span className="mono text-xs">{db.host}:{db.port}</span>} />
              <Row k="DB name" v={<span className="mono text-xs">{db.dbName}</span>} />
            </dl>
          ) : (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">Shared legacy database (no dedicated DB).</p>
          )}
        </Card>
      </div>

      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Recent activity" />
        {audit.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No recent activity recorded.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {audit.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400 w-20 flex-shrink-0 pt-0.5">{a.occurredAt ? formatDate(a.occurredAt) : '—'}</span>
                <span className="text-zinc-700 dark:text-zinc-200"><span className="mono text-xs">{a.action}</span> · {a.category}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SubscriptionTab({ sub, onChangePlan }: { sub: AdminBusinessDetail['subscription']; onChangePlan: () => void }) {
  if (!sub) {
    return (
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Subscription" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">No subscription on record for this business.</p>
      </Card>
    );
  }
  const tone =
    sub.status === 'active' ? 'success' : sub.status === 'trial' ? 'info' : sub.status === 'past_due' ? 'warning' : 'error';
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  return (
    <div className="space-y-4">
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Current plan" subtitle={`${sub.plan ?? '—'} · ${sub.interval}`} action={<Badge tone={tone}>{sub.status}</Badge>} />
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Price" v={taka(sub.unitAmountMinor)} />
          <Stat label="Interval" v={sub.interval} />
          <Stat label="Renews" v={sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'} />
        </div>
        {sub.trialEndsAt && (
          <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">Trial ends {formatDate(sub.trialEndsAt)}</div>
        )}
        <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
          <Button variant="secondary" size="sm" iconLeft={<ArrowLeftRight className="w-4 h-4" />} onClick={onChangePlan}>
            Change plan
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** Admin plan override: pick a published package + interval. Entitlements switch now;
 *  the new price bills from the next renewal (no proration, renewal date unchanged). */
function ChangePlanModal({ open, onClose, businessId, businessName, sub, onChanged }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  sub: AdminBusinessDetail['subscription'];
  onChanged: () => void;
}) {
  const { data: allPackages = [] } = useAdminAllPackages();
  const { data: allPrices = [] } = useAdminAllPrices();
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [packageId, setPackageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ plan: string; amountMinor: number } | null>(null);
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');

  const priceFor = (pkgId: string, iv: 'month' | 'year') =>
    allPrices.find((p) => p.packageId === pkgId && p.interval === iv && p.status === 'active');
  // Published platform/reseller packages that actually sell in the chosen interval.
  const choices = allPackages.filter((p) => p.status === 'published' && priceFor(p.id, interval));

  function close() {
    setPackageId(''); setInterval('month'); setBusy(false); setError(null); setDone(null);
    onClose();
  }
  async function submit() {
    if (!packageId) { setError('Pick a plan first.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await changeBusinessPlan(businessId, { packageId, interval });
      setDone({ plan: res.plan, amountMinor: res.amountMinor });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the plan.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title={done ? 'Plan changed' : 'Change subscription plan'}
      size="md"
      footer={done ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!packageId || busy} loading={busy}>Change plan</Button>
        </>
      )}
    >
      {done ? (
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          <span className="font-medium">{businessName}</span> is now on <span className="font-medium">{done.plan}</span>{' '}
          ({taka(done.amountMinor)}/{interval}). The new price bills from the next renewal.
        </p>
      ) : (
        <div className="space-y-4">
          {sub && (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Current: <span className="font-medium text-zinc-900 dark:text-zinc-100">{sub.plan ?? '—'}</span> · {taka(sub.unitAmountMinor)}/{sub.interval}
            </p>
          )}
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Billing interval</span>
            <div className="flex gap-2">
              {(['month', 'year'] as const).map((iv) => (
                <button
                  key={iv}
                  type="button"
                  onClick={() => { setInterval(iv); setPackageId(''); }}
                  className={`px-3 py-1.5 rounded-md text-sm border transition ${
                    interval === iv
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {iv === 'month' ? 'Monthly' : 'Yearly'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">New plan</span>
            {choices.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">No published packages with an active {interval === 'year' ? 'yearly' : 'monthly'} price.</p>
            ) : (
              <div className="space-y-2">
                {choices.map((p) => {
                  const price = priceFor(p.id, interval)!;
                  const isCurrent = sub?.plan === p.name && sub?.interval === interval;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPackageId(p.id)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border text-left transition ${
                        packageId === p.id
                          ? 'border-brand bg-brand-subtle dark:bg-brand/15 dark:border-brand-dm'
                          : 'border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-zinc-950 dark:text-zinc-50">
                          {p.name}
                          {isCurrent && <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Current</span>}
                        </span>
                        {p.tagline && <span className="block text-xs text-zinc-500 dark:text-zinc-400 truncate">{p.tagline}</span>}
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-zinc-950 dark:text-zinc-50 flex-shrink-0">
                        {taka(price.amount)}<span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">/{interval}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Limits and modules update immediately. The renewal date stays the same; the new price is billed from the next renewal — no proration.
          </p>
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

const PAY_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  paid: 'success', pending: 'warning', failed: 'error', refunded: 'neutral', partial_refunded: 'neutral', disputed: 'error',
};
const PAY_METHOD: Record<string, string> = {
  sslcommerz: 'SSLCommerz', manual_cash: 'Cash', bank_transfer: 'Bank Transfer', bkash: 'bKash', nagad: 'Nagad', stripe: 'Stripe', paypal: 'PayPal',
};

function PaymentsTab({ payments }: { payments: AdminBusinessDetail['payments'] }) {
  if (payments.length === 0) {
    return <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">No payments recorded for this business yet.</div>;
  }
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">Invoice</th>
            <th className="text-left font-medium px-4 py-3">Date</th>
            <th className="text-right font-medium px-4 py-3">Amount</th>
            <th className="text-left font-medium px-4 py-3">Method</th>
            <th className="text-left font-medium px-6 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {payments.map((r) => (
            <tr key={r.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3 mono text-xs text-zinc-700 dark:text-zinc-200">{r.invoiceNo}</td>
              <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{r.paidAt ? formatDate(r.paidAt) : '—'}</td>
              <td className="px-4 py-3 text-right">
                <Money minor={r.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
              </td>
              <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{PAY_METHOD[r.method] ?? r.method}</td>
              <td className="px-6 py-3">
                <Badge tone={PAY_TONE[r.status] ?? 'neutral'}>{r.status.replace(/_/g, ' ')}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DatabaseTab({ db }: { db: AdminBusinessDetail['database'] }) {
  if (!db) {
    return (
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Database" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          No dedicated database — this business runs on the shared legacy database (grandfathered). A dedicated DB is
          provisioned only for new managed businesses, or registered for self-hosted (BYO-DB).
        </p>
      </Card>
    );
  }
  const tone = db.status === 'ready' ? 'success' : db.status === 'failed' ? 'error' : 'warning';
  return (
    <Card className="p-5 border-zinc-200 dark:border-zinc-700">
      <CardHeader
        title={db.mode === 'self_hosted' ? 'Self-hosted database' : 'Managed database'}
        subtitle={db.provisionedAt ? `Provisioned ${formatDate(db.provisionedAt)}` : 'Not provisioned yet'}
        action={<Badge tone={tone}>{db.status}</Badge>}
      />
      <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        <Row k="Mode" v={db.mode === 'self_hosted' ? 'Self-hosted (BYO)' : 'Managed'} />
        <Row k="Status" v={db.status} />
        <Row k="Host" v={<span className="mono text-xs">{db.host}:{db.port}</span>} />
        <Row k="Database" v={<span className="mono text-xs">{db.dbName}</span>} />
        <Row k="Provisioned" v={db.provisionedAt ? formatDate(db.provisionedAt) : '—'} />
        <Row k="Attempts" v={String(db.attemptCount)} />
        {db.lastError && <Row k="Last error" v={<span className="text-red-600 dark:text-red-400 text-xs">{db.lastError}</span>} />}
      </dl>
    </Card>
  );
}

function DomainsTab({ domains }: { domains: AdminBusinessDetail['domains'] }) {
  if (domains.length === 0) {
    return (
      <div className="space-y-3">
        <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">
          No custom domains for this business — it uses the platform / reseller domain.
        </div>
        <Link to="/admin/domains">
          <Button variant="secondary" size="sm">Manage domains</Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {domains.map((d) => (
        <Card key={d.host} className="p-4 sm:p-5 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500"><Globe className="w-4 h-4" /></span>
              <div>
                <div className="font-medium text-zinc-950 dark:text-zinc-50 flex items-center gap-2">
                  {d.host}
                  {d.isPrimary && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Primary</span>}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">DNS {d.dnsStatus} · TLS {d.tlsStatus}</div>
              </div>
            </div>
            <Badge tone={d.dnsStatus === 'verified' ? 'success' : d.dnsStatus === 'failed' ? 'error' : 'warning'}>
              {d.dnsStatus === 'verified' ? 'Verified' : d.dnsStatus === 'failed' ? 'Failed' : 'Pending'}
            </Badge>
          </div>
        </Card>
      ))}
      <Link to="/admin/domains">
        <Button variant="secondary" size="sm">Manage domains</Button>
      </Link>
    </div>
  );
}

function UsersTab({ users, businessId, onChanged }: { users: AdminBusinessDetail['users']; businessId: string; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  type Member = AdminBusinessDetail['users'][number];
  const [editing, setEditing] = useState<Member | null>(null);
  const [resetting, setResetting] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-3">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">Owners and staff with POS access to this business.</p>
        <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setOpen(true)}>Add user</Button>
      </div>
      {users.length === 0 ? (
        <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">No users linked to this business yet.</div>
      ) : (
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-6 py-3">User</th>
                <th className="text-left font-medium px-4 py-3">Role</th>
                <th className="text-left font-medium px-4 py-3">Joined</th>
                <th className="text-right font-medium px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {users.map((u, i) => (
                <tr key={`${u.email}-${i}`} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-6 py-3">
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">
                      {u.name}
                      {u.status !== 'active' && <span className="ml-2 inline-block align-middle"><Badge tone={u.status === 'suspended' ? 'error' : 'neutral'}>{u.status}</Badge></span>}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                      {u.username ? <span className="font-mono text-zinc-600 dark:text-zinc-300">@{u.username}</span> : null}
                      {u.username && u.email && u.email !== u.username ? <span className="mx-1 text-zinc-300 dark:text-zinc-600">·</span> : null}
                      {u.email && u.email !== u.username ? u.email : null}
                      {!u.username && !u.email ? '—' : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 mono text-xs">
                    {u.role}
                    {u.posRole && <span className="text-zinc-400 dark:text-zinc-500"> · POS: {u.posRole}</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{u.joinedAt ? formatDate(u.joinedAt) : '—'}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="Edit user"
                        onClick={() => setEditing(u)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title={u.email ? 'Send password reset' : 'No email on file'}
                        disabled={!u.email}
                        onClick={() => setResetting(u)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-40 disabled:pointer-events-none"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title={u.role === 'owner' ? 'The owner cannot be removed' : 'Remove from business'}
                        disabled={u.role === 'owner'}
                        onClick={() => setRemoving(u)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:text-rose-400 dark:hover:bg-rose-950/40 transition disabled:opacity-40 disabled:pointer-events-none"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AddBusinessUserModal open={open} onClose={() => setOpen(false)} businessId={businessId} onChanged={onChanged} />
      <EditMemberModal member={editing} businessId={businessId} onClose={() => setEditing(null)} onChanged={onChanged} />
      <ResetPasswordModal member={resetting} onClose={() => setResetting(null)} />
      <RemoveMemberModal member={removing} businessId={businessId} onClose={() => setRemoving(null)} onChanged={onChanged} />
    </div>
  );
}

/** Edit a member's display name + portal role (owner's role is locked — transfer ownership instead). */
function EditMemberModal({ member, businessId, onClose, onChanged }: {
  member: AdminBusinessDetail['users'][number] | null;
  businessId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('staff');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sync fields when a row is picked (member changes null → row).
  const [seeded, setSeeded] = useState<string | null>(null);
  if (member && seeded !== member.id) {
    setName(member.name);
    setRole(member.role === 'owner' ? 'owner' : member.role);
    setError(null);
    setSeeded(member.id);
  }
  async function save() {
    if (!member) return;
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    setBusy(true); setError(null);
    try {
      await updateAdminBusinessUser(businessId, member.id, {
        name: name.trim(),
        ...(member.role !== 'owner' && role !== member.role ? { role: role as 'admin' | 'staff' | 'readonly' } : {}),
      });
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Edit user"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Full name" value={name} onChange={(e) => setName(e.target.value)} />
        {member?.role === 'owner' ? (
          <p className="text-xs text-zinc-500 dark:text-zinc-400">This user is the business owner — the role is locked.</p>
        ) : (
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Portal role</span>
            <div className="flex gap-2">
              {(['admin', 'staff', 'readonly'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition capitalize ${
                    role === r
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-zinc-500 dark:text-zinc-400">The POS role inside the POS itself is managed from the POS user settings.</p>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Email the member a password-reset (recovery) code — same flow as forgot-password. */
function ResetPasswordModal({ member, onClose }: { member: AdminBusinessDetail['users'][number] | null; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  function close() { setSentTo(null); setError(null); onClose(); }
  async function send() {
    if (!member) return;
    setBusy(true); setError(null);
    try {
      const res = await adminResetUserPassword(member.id);
      setSentTo(res.email);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={!!member}
      onClose={close}
      title="Reset password"
      size="sm"
      footer={sentTo ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={send} loading={busy}>Send reset email</Button>
        </>
      )}
    >
      {sentTo ? (
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          A password-reset code was emailed to <span className="font-medium">{sentTo}</span>.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            Email <span className="font-medium text-zinc-900 dark:text-zinc-100">{member?.email}</span> a secure
            password-reset code (the same flow as “Forgot password”).
          </p>
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/** Remove a member from this business. The account is deactivated only if this was its last scope. */
function RemoveMemberModal({ member, businessId, onClose, onChanged }: {
  member: AdminBusinessDetail['users'][number] | null;
  businessId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function remove() {
    if (!member) return;
    setBusy(true); setError(null);
    try {
      await removeAdminBusinessUser(businessId, member.id);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the user.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Remove user"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={remove} loading={busy}>Remove user</Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Remove <span className="font-medium text-zinc-900 dark:text-zinc-100">{member?.name}</span>{' '}
          ({member?.email || member?.username}) from this business? They lose portal + POS access here. If this is
          their only business, the account is deactivated (soft delete) — nothing is permanently erased.
        </p>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Edit the business's SaaS-side info (display name + owner contact email). */
function EditBusinessModal({ open, onClose, businessId, initialName, initialOwnerEmail, onChanged }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  initialName: string;
  initialOwnerEmail: string;
  onChanged: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [ownerEmail, setOwnerEmail] = useState(initialOwnerEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-seed when reopened for fresh values.
  const [wasOpen, setWasOpen] = useState(false);
  if (open && !wasOpen) {
    setName(initialName);
    setOwnerEmail(initialOwnerEmail);
    setError(null);
    setWasOpen(true);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }
  async function save() {
    if (!name.trim()) { setError('Business name cannot be empty.'); return; }
    setBusy(true); setError(null);
    try {
      await updateAdminBusinessInfo(businessId, { name: name.trim(), ownerEmail: ownerEmail.trim() || null });
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit business"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Business name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Owner email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} hint="Billing + renewal emails go here." />
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          This changes the SaaS-side record. The name shown inside the POS itself is a POS business setting.
        </p>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Admin: add a user to THIS business — Create (set a password now) or Invite (email a link). The
 *  chosen POS role maps to the Laravel role (Admin#<lbid>/Cashier#<lbid>) when pushed to the POS DB. */
function AddBusinessUserModal({ open, onClose, businessId, onChanged }: { open: boolean; onClose: () => void; businessId: string; onChanged: () => void }) {
  const [action, setAction] = useState<'create' | 'invite'>('invite');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [posRole, setPosRole] = useState<string>('Cashier');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | CreateUserResult | null>(null);

  function close() {
    setAction('invite'); setEmail(''); setName(''); setPosRole('Cashier'); setPassword(''); setBusy(false); setError(null); setResult(null);
    onClose();
  }
  async function submit() {
    if (!email.trim() || !businessId) { setError('A valid email is required.'); return; }
    if (action === 'create' && password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await createAdminBusinessUser(businessId, { action, email: email.trim(), name: name.trim() || undefined, posRole, password: action === 'create' ? password : undefined });
      setResult(res);
      onChanged();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? 'That person already has access to this business.' : status === 403 ? 'You do not have permission to add users here.' : action === 'create' ? 'Could not create the account.' : 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a user to this business'}
      size="md"
      footer={result ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={!email.trim() || busy} loading={busy}>{action === 'create' ? 'Create account' : 'Send invite'}</Button>
        </>
      )}
    >
      {result ? (
        'inviteUrl' in result ? <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} /> : <AccountCreatedPanel email={result.email} />
      ) : (
        <div className="space-y-4">
          <CreateOrInviteToggle action={action} onChange={setAction} />
          <Input label="Email" required type="email" placeholder="teammate@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Full name" placeholder="(optional)" value={name} onChange={(e) => setName(e.target.value)} />
          {action === 'create' && (
            <Input label="Password" required type="password" hint="Minimum 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
          <PosRoleSelector businessId={businessId} value={posRole} onChange={setPosRole} />
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

function AuditTab({ audit }: { audit: AdminBusinessDetail['audit'] }) {
  if (audit.length === 0) {
    return <div className="px-2 py-6 text-sm text-zinc-500 dark:text-zinc-400">No audit events recorded for this business yet.</div>;
  }
  return (
    <ul className="space-y-3">
      {audit.map((a) => (
        <li key={a.id} className="flex items-start gap-3 text-sm">
          <span className="w-7 h-7 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 flex-shrink-0 mt-0.5">
            <Database className="w-3.5 h-3.5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-zinc-900 dark:text-zinc-100">
              <span className="font-medium mono text-xs">{a.action}</span>
              <span className="text-zinc-500 dark:text-zinc-400"> · {a.category}</span>
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              {a.actorEmail} · {a.occurredAt ? timeAgo(a.occurredAt) : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-zinc-500 dark:text-zinc-400 w-28 flex-shrink-0">{k}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100 flex-1 min-w-0">{v}</dd>
    </div>
  );
}

function Stat({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mt-0.5 tabular-nums">{v}</div>
    </div>
  );
}
